import { ReadUnavailable, retryAfterMs, usePublicRead } from './public-reads';

export type TruthStatus = {
  productScope: 'arsenal-men-first-team';
  coverage: 'selected-matchbook' | 'premier-league-complete' | 'authorised-provider-snapshot';
  fixtureCount: number;
  checkedAt: string | null;
  lastCheckedAt: string | null;
  contentUpdatedAt?: string | null;
  staleAfter: string | null;
  version: number | null;
  provider: { id: string; label: string; source_url: string; rights_status: string; scope: string; last_reviewed_at: string; review_expires_at: string } | null;
  notices: Array<{ category: string; severity: 'info' | 'warning' | 'urgent'; subject: string }>;
};

export type SupporterSignalSnapshot = {
  capturedAt: string;
  phase: 'pre' | 'live' | 'ht' | 'ft' | 'post';
  minute: number | null;
  predictions: Partial<Record<'W' | 'D' | 'L', number>> | null;
  gauge: { average: number; moods: Partial<Record<'buzzing' | 'relieved' | 'anxious' | 'frustrated', number>> } | null;
  discussion: number | null;
};

type SignalFeed = { matchId: string; minimumCohort: number; snapshots: SupporterSignalSnapshot[] };

function useJson<T>(path: string, pollMs = 60 * 60_000): { data: T | null; loading: boolean } {
  return usePublicRead({
    key: `truth:${path}`,
    initial: () => ({ data: null as T | null, loading: false }),
    interval: () => pollMs,
    load: async () => {
      const response = await fetch(path, { cache: 'no-store', signal: AbortSignal.timeout(15_000) });
      if (!response.ok) throw new ReadUnavailable(response.status, retryAfterMs(response.headers.get('retry-after')));
      return { data: await response.json() as T, loading: false };
    },
  });
}

export function useTruthStatus() {
  return useJson<TruthStatus>('/api/truth/status', 15 * 60_000);
}

export function useSupporterSignal(matchId: string, enabled = true, activeWindow = false) {
  void enabled;
  void activeWindow;
  // Public Signal is deliberately withheld. Keep the stable empty shape local
  // so match pages do not poll an endpoint that cannot publish any readings.
  return { data: { matchId, minimumCohort: 5, snapshots: [] } satisfies SignalFeed, loading: false };
}
